/**
 * Shared helpers for resolving {{ }} placeholders in saved message scripts.
 *
 * Used by both the Executive Assistant Schedule modal (where the "assistant"
 * is the selected ExecutiveAssistantProfile) and the Unibox compose modal
 * (where the "sender" is the signed-in user / selected From account).
 *
 * Keeping the substitution rules in one place so the script library reads the
 * same on every surface where users insert one.
 */

export interface ScriptPlaceholderContext {
  /** First name parsed from the To field. Used for {{firstName}} / {{recipientFirstName}}. */
  recipientFirstName: string;
  /** Full sender name. Used for {{assistantName}}, {{senderName}}, and aliases. */
  senderFullName: string;
  /** Sender first name. Used for {{assistantFirstName}}. */
  senderFirstName: string;
}

/**
 * Extract a recipient first name from a raw "Name <email>" string, a bare
 * email, or a plain display name. Falls back to the email local-part token
 * before any `._+-` separator.
 */
export function extractFirstNameFromAddressOrName(raw: string): string {
  const value = String(raw || '').trim();
  if (!value) return '';

  // "Display Name <addr@host>"
  const m = value.match(/^\s*(.+?)\s*<([^>]+)>\s*$/);
  const display = m ? m[1].replace(/^["']|["']$/g, '').trim() : '';
  const emailAddr = m
    ? m[2].trim()
    : value.includes('@') && !value.includes('<')
    ? value.trim()
    : '';

  if (display) {
    if (display.includes(',')) {
      const after = display.split(',')[1]?.trim();
      if (after) return after.split(/\s+/)[0] || '';
    }
    return display.split(/\s+/)[0] || '';
  }
  if (emailAddr.includes('@')) {
    const local = emailAddr.split('@')[0] || '';
    return (local.split(/[._+-]/)[0] || local).trim();
  }
  // Plain display name (no email)
  return value.split(/\s+/)[0] || '';
}

/**
 * Replace placeholder tokens in `text` using the supplied context. Unknown
 * placeholders are left untouched so unrelated `{{ }}` blocks survive.
 *
 * Recognized tokens (case-insensitive, whitespace-tolerant inside braces):
 *   {{firstName}}, {{recipientFirstName}}             — recipient first name
 *   {{assistantName}}, {{senderName}}                 — full sender name
 *   {{executiveAssistantName}}, {{excutiveAssistantname}} (typo alias)
 *   {{assistantFirstName}}                            — sender first name
 */
export function applyScriptPlaceholders(
  text: string,
  ctx: ScriptPlaceholderContext
): string {
  const r = ctx.recipientFirstName || '';
  const af = ctx.senderFullName || '';
  const afn = ctx.senderFirstName || '';
  return String(text || '')
    .replace(/\{\{\s*firstName\s*\}\}/gi, r)
    .replace(/\{\{\s*recipientFirstName\s*\}\}/gi, r)
    .replace(/\{\{\s*assistantName\s*\}\}/gi, af)
    .replace(/\{\{\s*assistantFirstName\s*\}\}/gi, afn)
    .replace(/\{\{\s*senderName\s*\}\}/gi, af)
    .replace(/\{\{\s*executiveAssistantName\s*\}\}/gi, af)
    .replace(/\{\{\s*excutiveAssistantname\s*\}\}/gi, af);
}
